import {
  camelCaseToKebab,
  camelCaseToSnakeCase,
  camelCaseToWords,
  capitalizeFirst,
  lowercaseFirst,
  snakeCaseToWords,
  snakeToTitleCase,
  titleCase,
  titleCaseMultipleWords,
  uppercaseAbbreviations,
  wordsToKebabCase,
  wordsToSnakeCase,
} from "@/utils/text-case";
import { memoize, range } from "lodash";

export const pluralize = (count: number, singular: string, plural: string): string =>
  count === 1 ? singular : plural;

/**
 * A reminder to use this character instead of typing out ...
 */
export const ELLIPSIS_CHARACTER = "…";

export {
  camelCaseToKebab,
  camelCaseToSnakeCase,
  camelCaseToWords,
  capitalizeFirst,
  lowercaseFirst,
  snakeCaseToWords,
  snakeToTitleCase,
  titleCase,
  titleCaseMultipleWords,
  uppercaseAbbreviations,
  wordsToKebabCase,
  wordsToSnakeCase,
};

interface PrimitiveConvertible {
  [Symbol.toPrimitive]?: (hint: "string") => string | number | bigint;
  toString?: () => string;
}

export const abbreviateTitleId = (id?: string) => {
  if (id == null || id.length === 0) {
    return "";
  }

  const firstSegment = id.split("-")[0];
  return (firstSegment || id).slice(0, 8);
};

export const hasNoLeadingOrTrailingWhitespace = (value: string) => value === value.trim();

export const arbitraryStringFromDecimalValue = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "bigint") {
    return `${value}`;
  }

  if (typeof value !== "object") {
    return null;
  }

  const convertible = value as PrimitiveConvertible;
  const toPrimitive = convertible[Symbol.toPrimitive];
  if (typeof toPrimitive === "function") {
    return `${toPrimitive.call(convertible, "string")}`;
  }

  if (
    typeof convertible.toString === "function" &&
    convertible.toString !== Object.prototype.toString
  ) {
    return convertible.toString();
  }

  return null;
};

/**
 * Compare two version strings of the form "x.y.z" where x, y, and z are integers.
 */
export function compareVersionStrings(a: string, b: string): number {
  const aParts = a.split(".");
  const bParts = b.split(".");
  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const numA = aParts[i] != null ? parseInt(aParts[i]) : 0;
    const numB = bParts[i] != null ? parseInt(bParts[i]) : 0;
    if (numA !== numB) {
      return numA - numB; // Ascending order
    }
  }
  return 0;
}

export const onlyLastPath = (str: string, delimiter = "."): string => {
  return str.split(delimiter).at(-1) ?? "";
};

const makeLargeNumberFormat = memoize(
  (digits = 2) =>
    new Intl.NumberFormat("en-us", {
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: digits,
    }),
);

export const getLineCount = (input: string): number => {
  return (input.match(/\r\n|\r|\n/g) || "").length + 1;
};

export const shortFormatNumber = (input: number, fixedDigitCount = 2): string => {
  if (input === 0) {
    return "0";
  }
  // Shorten the magnitude and put the sign back on, so a negative value gets the same treatment as
  // its positive twin instead of falling through to full float precision.
  if (input < 0) {
    return `-${shortFormatNumber(-input, fixedDigitCount)}`;
  }

  if (input < 1e-3) {
    return input.toExponential(fixedDigitCount);
  } else if (input < 1e3) {
    if (Number.isInteger(input)) {
      return input.toString();
    } else {
      return input.toFixed(fixedDigitCount);
    }
  } else if (input < 1e15) {
    return makeLargeNumberFormat(fixedDigitCount).format(input);
  } else {
    return input.toExponential(fixedDigitCount);
  }
};

// 3 significant figures keeps the mantissa to <= 4 chars.
const compactCountFormat = new Intl.NumberFormat("en-us", {
  notation: "compact",
  compactDisplay: "short",
  maximumSignificantDigits: 3,
});

// Formats a non-negative count compactly: shown in full below 10,000 (up to 4
// digits), abbreviated to 3 significant figures above that (134101 -> "134K",
// 1_310_000 -> "1.31M", 131_123_123 -> "131M").
export const formatCompactCount = (value: number): string =>
  value < 1e4 ? String(value) : compactCountFormat.format(value);

/** The same 3 significant figures at every magnitude, for a column that has to read at one precision
 *  whether or not its values are whole (7600 -> "7.6K", 3.14159 -> "3.14"). */
export const formatCompactNumber = (value: number): string => compactCountFormat.format(value);

const intFormat = new Intl.NumberFormat("en-us");

export const formatInt = (input: number | bigint) => {
  return intFormat.format(input);
};

const twoDigitFormat = new Intl.NumberFormat("en-us", { maximumFractionDigits: 2 });
const noDigitFormat = new Intl.NumberFormat("en-us", { maximumFractionDigits: 0 });

export const formatTwoDigit = (input: number) => {
  return twoDigitFormat.format(input);
};

export const prettyFormatUsage = (input: number) => {
  return input > 0 && input < 0.01 ? "<.01" : twoDigitFormat.format(input);
};

export const formatTwoDigitWhenSmall = (input: number, threshold = 1) => {
  return input < threshold ? twoDigitFormat.format(input) : noDigitFormat.format(Math.trunc(input));
};

export const sanitizeDocumentationComment = (doc: string): string => {
  return (
    doc
      // replace newline followed by any number of spaces with just a newline
      .replace(/\n[ \t]+/g, "\n")
      // replace starting/ending newlines and starting spaces/tabs with nothing
      .replace(/^\n+|\n+$|^[ \t]+/g, "")
  );
};

export interface PrettyFormatBytesOpts {
  binary?: boolean;
  decimals?: number;
}
/**
 * Format a number of bytes into a human-readable string. Setting `binary` to true
 * will use 1024 as the base for the conversion, and the resulting unit will be `KiB`, `MiB`, etc.
 * Otherwise it will use 1000 as the base and the resulting unit will be `KB`, `MB`, etc.
 */
export function prettyFormatBytes(
  bytes: number | bigint,
  opts: PrettyFormatBytesOpts = { binary: false, decimals: 2 },
): string {
  const bytesNum = Number(bytes);
  if (!+bytesNum) return "0 bytes";

  const { binary, decimals } = opts;

  const k = binary ? 1024 : 1000;
  const dm = Math.max(decimals ?? 2, 0);
  const sizes = binary
    ? ["bytes", "KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"]
    : ["bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytesNum) / Math.log(k));

  return `${parseFloat((bytesNum / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

const idRegex = new RegExp(/(?:_|\.)id(?:_*)$/);

/**
 * Guess whether this fqn is an id. The use case for this is that we don't want to
 * pretty-format numeric id features with commas e.g. user_id 1234 should not be rendered as 1,234
 * but user_dog_count 1234 should probably be rendered as 1,234
 */
export function fqnIsProbablyIdentifier(fqn: string): boolean {
  return idRegex.test(fqn);
}

export type JsonType =
  | string
  | number
  | bigint
  | boolean
  | null
  | { [property: string]: JsonType | null }
  | JsonType[];

export const truncateString = (str: string, truncLimit: number): string => {
  if (str.length >= truncLimit) {
    return `${str.slice(0, truncLimit - 1)}${ELLIPSIS_CHARACTER}`;
  }

  return str;
};

export const nonCollapsingSpaces = (str: string): string => {
  return str.replaceAll(" ", "\u00A0");
};

const CHAR_OFFSET = 65;
const ALPHABET_SIZE = 26;

/**
 * Given a count of existing objects in some collection, and knowing that
 * we are going to add a brand new object to that collection, generate a
 * new alphabetical ID for that object
 */
export function generateSimpleIdSequence(existingCount: number): string {
  const charCode = existingCount % ALPHABET_SIZE;
  const charCount = Math.trunc(existingCount / ALPHABET_SIZE);
  const char = String.fromCharCode(charCode + CHAR_OFFSET);
  return wrappedCharSequence(char, charCount + 1);
}

function wrappedCharSequence(char: string, length: number): string {
  const charCode = char.codePointAt(0)!;
  const offset = charCode - CHAR_OFFSET;
  const codes = range(0, length).map((i) => (offset + i) % ALPHABET_SIZE);
  return codes.map((c) => String.fromCharCode(c + CHAR_OFFSET)).join("");
}

const LCARS_CHAR_SIZE_ARRAY = [
  0, 5.328125, 5.328125, 5.328125, 5.328125, 5.328125, 5.328125, 5.328125, 0, 0, 0, 0, 0, 0,
  5.328125, 5.328125, 5.328125, 5.328125, 5.328125, 5.328125, 5.328125, 5.328125, 5.328125,
  5.328125, 5.328125, 5.328125, 5.328125, 5.328125, 5.328125, 0, 5.328125, 5.328125, 4, 4.484375,
  6.078125, 12.796875, 10.59375, 14.796875, 12.203125, 3.5625, 5.015625, 5.015625, 7.125, 10.09375,
  4.328125, 7.203125, 4.328125, 7.796875, 11.328125, 6.125, 10.5, 10.46875, 10.71875, 10.34375,
  11.046875, 8.75, 10.953125, 11.046875, 4.328125, 4.328125, 9.390625, 10.3125, 9.390625, 8.90625,
  15.5625, 12.15625, 11.40625, 12.484375, 12.515625, 10.5, 9.984375, 13.21875, 12.640625, 4.65625,
  6, 11.125, 9, 16.84375, 13.5, 13.1875, 11.140625, 13.25, 11.390625, 11.078125, 10.296875,
  12.34375, 11.859375, 18.125, 11.78125, 10.8125, 11.03125, 5.296875, 7.796875, 5.296875, 8.828125,
  9.125, 4.828125, 9.328125, 10.21875, 9.390625, 10.21875, 9.53125, 6.03125, 10.21875, 9.640625,
  4.078125, 4.078125, 8.828125, 4.078125, 14.796875, 9.640625, 9.75, 10.21875, 10.21875, 5.96875,
  8.484375, 6.21875, 9.5625, 9.203125, 14.25, 8.8125, 8.96875, 8.25, 5.5, 4.421875, 5.5, 10.65625,
];

export function staticEstimateSVGTextWidth(text: string, fontSize: number) {
  let width = 0;
  const scaleFactor = fontSize / 100;

  for (let i = 0; i < text.length; i++) {
    width = width + (LCARS_CHAR_SIZE_ARRAY[text.charCodeAt(i)] || 0);
  }

  return width * scaleFactor;
}

export const stringToSafeBoolean = (maybeStr: unknown): boolean | undefined => {
  if (maybeStr === "true" || maybeStr === "t") {
    return true;
  } else if (maybeStr === "false" || maybeStr === "f") {
    return false;
  } else {
    return undefined;
  }
};

export const isNumericString = (str: string) => {
  return !isNaN(str as unknown as number);
};

// Barebones way to check that the customer is not passing in wrong formatted
// data
const RSA_REGEX = /^-----.+-----.+-----END RSA PRIVATE KEY-----$/s;
export const isRSAPrivateKey = (str: string): boolean => {
  return RSA_REGEX.test(str);
};

export const withScreenFriendlySpace = (str: string) => str.replace(/ /g, "\u00A0");

/**
 * Parse Kubernetes resource quantities into their numeric equivalents. (AI-generated)
 * Supports suffixes: m (milli), k (kilo), M (mega), G (giga), T (tera), P (peta), E (exa)
 * Examples: "100m" -> 0.1, "1k" -> 1000, "1.5G" -> 1500000000
 * @param quantity The Kubernetes quantity string to parse (e.g., "100m", "1k", "1.5G")
 * @returns The numeric value or NaN if parsing fails
 */
export function parseKubernetesQuantity(quantity: string): number {
  if (!quantity || quantity.trim() === "") {
    return NaN;
  }

  const regex = /^([0-9]+(?:\.[0-9]+)?)([mMkGTPE]*)$/;
  const match = quantity.trim().match(regex);

  if (!match) {
    return NaN;
  }

  const value = parseFloat(match[1]);
  const suffix = match[2];

  if (isNaN(value)) {
    return NaN;
  }

  switch (suffix) {
    case "m":
      return value / 1e3; // milli (0.001)
    case "k":
      return value * 1e3; // kilo (1,000)
    case "M":
      return value * 1e6; // mega (1,000,000)
    case "G":
      return value * 1e9; // giga (1,000,000,000)
    case "T":
      return value * 1e12; // tera (1,000,000,000,000)
    case "P":
      return value * 1e15; // peta (10^15)
    case "E":
      return value * 1e18; // exa (10^18)
    default:
      return value; // No suffix
  }
}

export const isISOString = (maybeIso: string): boolean => {
  if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})/.test(maybeIso))
    return false;
  const d = new Date(maybeIso);
  return !isNaN(d.getTime()); // valid date
};

export const stripPrefix = (str: string, prefix: string): string => {
  if (str.startsWith(prefix)) {
    return str.slice(prefix.length);
  }

  return str;
};

export const stripSuffix = (str: string, suffix: string): string => {
  if (str.endsWith(suffix)) {
    return str.slice(0, -suffix.length);
  }

  return str;
};

/**
 * Appends the suffix if is not already appended
 */
export const appendMaybeSuffix = (str: string, suffix: string): string => {
  if (str.endsWith(suffix)) {
    return str;
  }

  return str;
};

export const isNonEmptyString = (value: unknown): value is string => {
  return value != null && typeof value === "string" && value.trim() !== "";
};
